import {
  ComplianceStatus,
  ConfigurationStatus,
  type InventoryResource,
} from '@repo/api-gw-sdk';

export interface ExtraInformationDescription {
  entitySpecifyingField: (entity: InventoryResource) => string | undefined;
  descriptions: Record<string, string>;
}

export const LIMIT_EXCEEDED_DESCRIPTION = `This resource is out of scope because it exceeds one or more configured limits, such as resource size, object count, or the maximum number of resources for its type.`;

export const LIMIT_EXCEEDED_QUOTA_DESCRIPTIONS: ExtraInformationDescription = {
  entitySpecifyingField: (entity) => entity.quota?.quotaStatus,
  descriptions: {
    SIZE_EXCEEDED: `Backup is disabled because this resource is over the maximum size limit. Contact Eon Support to increase your limit.`,
    OBJECT_COUNT_EXCEEDED: `Backup is disabled because this resource is over the maximum object count limit. Contact Eon Support to increase your limit.`,
    MAX_RESOURCES_EXCEEDED: `Backup is disabled because you've reached the maximum number of resources for this type. To enable this resource in backup, first exclude another resource.`,
  },
};

interface ConfigurationStatusLook {
  name: string;
  description: string;
  icon: string;
  iconColor: string;
  backgroundColor: string;
  extraInformationDescription?: ExtraInformationDescription;
}

interface ComplianceStatusLook {
  name: string;
  icon: string;
  iconColor: string;
  backgroundColor: string;
}

const SUCCESS_LOOK = {
  iconColor: 'var(--mui-palette-success-main)',
  backgroundColor: 'var(--mui-palette-success-opacity)',
};
const ERROR_LOOK = {
  iconColor: 'var(--mui-palette-error-main)',
  backgroundColor: 'var(--mui-palette-error-opacity)',
};
const INFO_LOOK = {
  iconColor: 'var(--mui-palette-info-main)',
  backgroundColor: 'var(--mui-palette-info-opacity)',
};
const NEUTRAL_LOOK = {
  iconColor: 'var(--mui-palette-custom-GREY-01)',
  backgroundColor: 'var(--mui-palette-custom-GREY-OPACITY)',
};
const VIOLATIONS_LOOK = {
  iconColor: 'var(--mui-palette-custom-GRADIENT-VIOLATIONS-ICON)',
  backgroundColor: 'var(--mui-palette-custom-GRADIENT-VIOLATIONS)',
};

// Green is backed up, red is a resource that should be backed up and is not, blue is in progress,
// and grey is out of scope or gone. Labels differ from the posture's where this axis names a state
// differently: UNASSIGNED is "Not backed up" there.
export const ConfigurationStatuses: Record<
  ConfigurationStatus,
  ConfigurationStatusLook
> = {
  [ConfigurationStatus.CONFIGURATION_STATUS_ASSIGNED]: {
    name: 'Backup assigned',
    description: 'A backup policy applies to this resource.',
    icon: 'material-symbols-check-circle-outline-rounded',
    ...SUCCESS_LOOK,
  },
  [ConfigurationStatus.CONFIGURATION_STATUS_UNASSIGNED]: {
    name: 'Backup not assigned',
    description:
      'No backup policy applies to this resource, and it is not excluded from backup. We recommend updating your backup policies to include it.',
    icon: 'material-symbols-disc-full-outline-rounded',
    ...ERROR_LOOK,
  },
  [ConfigurationStatus.CONFIGURATION_STATUS_INITIAL_CLASSIFICATION]: {
    name: 'Initial classification',
    description:
      'This resource was recently discovered and is being scanned by Eon for the first time. Its status will be updated after the scan is done.',
    icon: 'material-symbols-clock-loader-40',
    ...INFO_LOOK,
  },
  [ConfigurationStatus.CONFIGURATION_STATUS_ACCESS_LOST]: {
    name: 'Access lost',
    description: `Eon can no longer access this resource's source account, so it's not being backed up. Go to Backup settings and review the source account's configuration to restore access, or disconnect the account there if you meant to stop backing it up. Existing Eon snapshots are still available to explore and restore.`,
    icon: 'material-symbols-wifi-tethering-off',
    ...ERROR_LOOK,
  },
  [ConfigurationStatus.CONFIGURATION_STATUS_CLASSIFICATION_FAILED]: {
    name: 'Initial classification failed',
    description: `Eon couldn't classify this resource after repeated scan attempts, so it can't be backed up. Contact Eon Support to enable backing it up.`,
    icon: 'material-symbols-running-with-errors',
    ...ERROR_LOOK,
  },
  [ConfigurationStatus.CONFIGURATION_STATUS_TERMINATED]: {
    name: 'Terminated',
    description:
      'This resource was terminated in your source account. It may have Eon snapshots or cloud recovery points you can explore and restore.',
    icon: 'material-symbols-do-not-disturb-on-outline-rounded',
    ...NEUTRAL_LOOK,
  },
  [ConfigurationStatus.CONFIGURATION_STATUS_DISCONNECTED]: {
    name: 'Disconnected',
    description: `This resource's source account was disconnected from Eon. It still has Eon snapshots you can explore and restore.`,
    icon: 'material-symbols-error-med-outline-rounded',
    ...NEUTRAL_LOOK,
  },
  [ConfigurationStatus.CONFIGURATION_STATUS_EXCLUDED_FROM_BACKUP]: {
    name: 'Excluded',
    description:
      'A team member asked Eon not to back up this resource. It won’t trigger any alerts for backup issues.',
    icon: 'material-symbols-hide-source-outline-rounded',
    ...NEUTRAL_LOOK,
  },
  [ConfigurationStatus.CONFIGURATION_STATUS_LIMIT_EXCEEDED]: {
    name: 'Out of scope',
    description: LIMIT_EXCEEDED_DESCRIPTION,
    icon: 'material-symbols-readiness-score-outline',
    ...NEUTRAL_LOOK,
    extraInformationDescription: LIMIT_EXCEEDED_QUOTA_DESCRIPTIONS,
  },
  [ConfigurationStatus.CONFIGURATION_STATUS_UNSUPPORTED]: {
    name: 'Unsupported',
    description: `This resource isn't supported for backup by Eon.`,
    icon: 'material-symbols-cancel-outline',
    ...NEUTRAL_LOOK,
  },
};

export const ComplianceStatuses: Record<
  ComplianceStatus,
  ComplianceStatusLook
> = {
  [ComplianceStatus.COMPLIANCE_STATUS_COMPLIANT]: {
    name: 'Compliant',
    icon: 'material-symbols-shield-outline-rounded',
    ...SUCCESS_LOOK,
  },
  [ComplianceStatus.COMPLIANCE_STATUS_VIOLATIONS]: {
    name: 'Violations',
    icon: 'material-symbols-error-outline-rounded',
    ...VIOLATIONS_LOOK,
  },
  [ComplianceStatus.COMPLIANCE_STATUS_ALL_MUTED]: {
    name: 'All muted',
    icon: 'material-symbols-motion-photos-paused-outline-rounded',
    ...NEUTRAL_LOOK,
  },
};
